import React from 'react';
import {
  AuditOutlined,
  DashboardOutlined,
  FileSearchOutlined,
  FileTextOutlined,
  PieChartOutlined,
  RadarChartOutlined,
  ThunderboltOutlined
} from '@ant-design/icons';

/**
 * Frontend `code → icon` registry for deliverable service types (ADR-0003 Decision 1).
 *
 * Icons ONLY — the label text always comes from the API (`serviceTypeLabel` /
 * `lov_service_type.label`), never from here, and there is no blurb (every
 * `lov_service_type.description` is NULL today). Uses `@ant-design/icons` (the
 * app's existing icon library); `lucide-react` is intentionally NOT a dependency.
 *
 * Covers the six known active catalog codes; unmapped codes fall back to a
 * generic file icon so a newly-added service type still renders.
 */
type AntdIconComponent = typeof FileTextOutlined;

const SERVICE_TYPE_ICONS: Record<string, AntdIconComponent> = {
  SVC_001: DashboardOutlined, // FMV Report
  SVC_002: ThunderboltOutlined, // Quick Check
  SVC_003: FileSearchOutlined, // Research Request
  SVC_004: AuditOutlined, // Contract Forensic Analysis
  SVC_005: RadarChartOutlined, // PRISM
  SVC_006: PieChartOutlined // Portfolio Analysis
};

/** Default icon for any code not present in the registry. */
const DEFAULT_SERVICE_TYPE_ICON: AntdIconComponent = FileTextOutlined;

/**
 * Renders the icon for a deliverable service-type code. Falls back to a generic
 * file icon for unmapped codes (e.g. a future catalog entry).
 * @param {object} props - Component props.
 * @param {string} props.code - Service type code (e.g. `SVC_001`).
 * @param {string} [props.className] - Optional className forwarded to the icon.
 * @returns {React.ReactElement} The mapped (or default) Ant Design icon.
 */
export function ServiceTypeIcon({ code, className }: { code: string; className?: string }): React.ReactElement {
  const Icon = SERVICE_TYPE_ICONS[code] ?? DEFAULT_SERVICE_TYPE_ICON;
  return <Icon className={className} aria-hidden="true" />;
}
